import anonymousApiClient from '@/Services/Clients/anonymousApiClient';
import apiClient from '@/Services/Clients/apiClient';
import { ApplicationError } from '@/Services/Clients/applicationError';
import Upload from '@/Types/upload';
import UpdateUploadModel from '@/Types/uploadServiceModels/updateUploadModel';
import { UploadParentItemType } from '@/Types/Enums/UploadParentItemType';
import logger from '@/Utils/logger';
import GetUploadMetadataRequest from '@/VueComponents/Uploads/models/getUploadMetadataRequest';
import GetUploadMetadataResponse from '@/VueComponents/Uploads/models/getUploadMetadataResponse';
import SaveUploadMetadataRequest from '@/VueComponents/Uploads/models/saveUploadMetadataRequest';
import moment from 'moment/moment';
import UploadTypeResolver from '@/Utils/uploadTypeResolver';
import BaseUploadModel from '@/Models/baseUploadModel';
import UploadSelectorFileUploadModel from '@/VueComponents/FileUploadSelector/models/uploadSelectorFileUploadModel';
import { useUploadManagerStore } from '@/VueCore/stores/uploadManagerStore';
import { UploadStatus } from '@/Types/Enums/uploadStatus';
import UploadModel from '@/Models/uploadModel';

const UPLOAD_BASE_URL: string = 'api/upload';

export default class UploadService {

  public static getUploadsByRequestId(requestId: string): Promise<Upload[]> {
    const resourceUrl = `${UPLOAD_BASE_URL}/?requestId=${requestId}`;
    return apiClient.get(resourceUrl);
  }

  public static getUploadsByResponseId(responseId: string): Promise<Upload[]> {
    const resourceUrl = `${UPLOAD_BASE_URL}/response/?responseId=${responseId}`;
    return apiClient.get(resourceUrl);
  }

  public static updateUploads(uploads: UpdateUploadModel[]): Promise<void> {

    if (uploads.length === 0) {
      return Promise.resolve();
    }

    const resourceUrl = `${UPLOAD_BASE_URL}/updateItems`;

    return apiClient.put(resourceUrl, uploads);
  }

  public static deleteUploads(uploads: BaseUploadModel[]): Promise<void> {

    const resourceUrl = `${UPLOAD_BASE_URL}/deleteItems`;
    const singleFileUploads =
      uploads.flatMap(u => u.isFolder ? u.children : [u]);
    const uploadManager = useUploadManagerStore();

    for (const upload of singleFileUploads) {
      if (upload.statusName === UploadStatus.Pending ||
        upload.statusName === UploadStatus.Uploading) {

        uploadManager.cancelUpload(upload as UploadModel);
      }
    }

    // If the upload failed authorisation then there is no upload to delete.
    const uploadsForDelete = uploads.filter(upload => upload.statusName !== UploadStatus.FailedAuthorisation)
      .map(upload => upload.uploadId);

    // If there is no upload to delete so return with a resolved promise.
    // For uploads that are authorising the user can't remove the upload.
    if (uploadsForDelete.length === 0) {
      return Promise.resolve();
    }

    return apiClient.post(resourceUrl, uploadsForDelete);
  }

  // TODO: Remove when partner request upload file selector is reworked to use new generic FileSelector components
  public static addUploadsAsFiles(fileList: FileList | File[], parentItemId: string,
    parentItemType: UploadParentItemType,
    initMetadata: boolean,
    addUploadsFailedCallback: (e: any) => void) {

    const files = Array.from(fileList);
    const uploadModels: UploadModel[] =
      files.map(f => UploadModel.fromFile(f, UploadTypeResolver.getTypeFromNativeFile(f)));

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    UploadService.authorizeUploads(uploadModels, parentItemId, parentItemType, initMetadata)
      .catch(e => {
        addUploadsFailedCallback(e);
      });

    return uploadModels;

  }

  public static addUploads(files: UploadSelectorFileUploadModel[], parentItemId: string,
    parentItemType: UploadParentItemType,
    initMetadata: boolean,
    addUploadsFailedCallback: (e: any) => void): UploadModel[] {

    const uploadModels: UploadModel[] =
      files.map(f => UploadModel.fromFile(f.file, UploadTypeResolver.getFromFileUploadType(f.type)));

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    UploadService.authorizeUploads(uploadModels, parentItemId, parentItemType, initMetadata)
      .catch(e => {
        addUploadsFailedCallback(e);
      });

    return uploadModels;
  }

  public static setUploadFailed(uploadToken: string): Promise<void> {
    const uri = `${UPLOAD_BASE_URL}/setFailed/${uploadToken}`;
    return apiClient.put(uri);
  }

  public static uploadComplete(uploadToken: string): Promise<void> {
    const resourceUrl = `${UPLOAD_BASE_URL}/complete/${uploadToken}`;
    return anonymousApiClient.put(resourceUrl);
  }

  public static getUploadMetadataAndConfigurableFields(model: GetUploadMetadataRequest,
    uploadParentItemType: UploadParentItemType): Promise<GetUploadMetadataResponse> {
    const parentItemType = uploadParentItemType === UploadParentItemType.selfResponse ?
      'response' : 'request';
    const resourceUrl = `${UPLOAD_BASE_URL}/${parentItemType}/metadata`;
    return apiClient.post(resourceUrl, model);
  }

  public static saveUploadsMetadata(model: SaveUploadMetadataRequest,
    uploadParentItemType: UploadParentItemType): Promise<{ deletedConfigurableFieldNames: string[] }> {
    const parentItemType = uploadParentItemType === UploadParentItemType.selfResponse ?
      'response' : 'request';
    const resourceUrl = `${UPLOAD_BASE_URL}/${parentItemType}/metadata`;
    return apiClient.put(resourceUrl, model);
  }

  private static async authorizeUploads(uploads: UploadModel[], requestId: string,
    uploadParentItemType: UploadParentItemType, initMetadata: boolean) {
    try {
      const serverUploads = await UploadService.postNewUploadsToServer(uploads, requestId, uploadParentItemType);

      // Files returned from the server are in the same order
      for (let i = 0; i< serverUploads.length; i++) {
        uploads[i].updateWithServerData(serverUploads[i]);
      }

      const uploadManager = useUploadManagerStore();

      if (initMetadata) {
        await UploadService.tryInitMetadata(uploads, requestId, uploadParentItemType);
      }

      uploads.forEach(u => uploadManager.addUpload(u));
    } catch (e) {
      uploads.forEach(um => um.statusName = UploadStatus.FailedAuthorisation);
      throw e;
    }
  }

  private static postNewUploadsToServer(uploads: UploadModel[],
    requestId: string,
    uploadParentItemType: UploadParentItemType): Promise<Upload[]> {
    const resourceUrl = this.getNewUploadsUrl(uploadParentItemType);

    const data = {
      parentItemId: requestId,
      uploads: uploads.map(u => {
        return {
          fileName: u.fileName,
          fileSize: u.size,
          lastWriteDate: moment(u.nativeBrowserFile.lastModified ||
            u.nativeBrowserFile.lastModifiedDate).toISOString(),
          fileRelativePath: u.fileRelativePath,
          type: u.type
        };
      })
    };

    return apiClient.post(resourceUrl, data);
  }

  private static getNewUploadsUrl(uploadParentItemType: UploadParentItemType): string {
    const parentItemType = uploadParentItemType === UploadParentItemType.selfResponse ?
      'response' : 'request';
    return `${UPLOAD_BASE_URL}/uploadItems/${parentItemType}`;
  }

  private static initMetadata(uploads: UploadModel[],
    parentItemId: string,
    uploadParentItemType: UploadParentItemType): Promise<void> {
    const parentItemType = uploadParentItemType === UploadParentItemType.selfResponse ?
      'response' : 'request';
    const resourceUrl = `${UPLOAD_BASE_URL}/${parentItemType}/metadata/init`;

    const data = {
      parentId: parentItemId,
      uploads: uploads.map(file => {
        return {
          id: file.uploadId,
          name: file.fileName,
          type: file.type
        };
      })
    };

    return apiClient.post(resourceUrl, data);
  }

  private static async tryInitMetadata(uploads: UploadModel[],
    parentItemId: string, uploadParentItemType: UploadParentItemType): Promise<void> {
    try {
      await UploadService.initMetadata(uploads, parentItemId, uploadParentItemType);
    } catch (error) {
      const applicationError = error as ApplicationError;
      if (applicationError.errorData?.errorMessages?.length) {
        logger.untranslatedWarning(applicationError.errorData?.errorMessages[0] ?? '');
      } else {
        logger.warning('FailedToInitUploadMetadata');
      }
    }
  }
}